// SL1.0's ellipsis menu on the RBP Shopping List (`rbp_report.htm:133-141`, the card `:92-99`;
// the report header `rbp_report_header.htm:66-72`): a three-dot button opening a short list of
// actions. Built on the WAI-ARIA menu button pattern: the trigger names the menu and says whether
// it is open; the menu takes focus on its first item, the arrow keys, Home and End move between
// items, Escape closes it and puts focus back on the trigger, and a click outside or Tab closes it.
// There is no Figma frame and the design system has no menu or three-dot icon, so the glyph is drawn
// in CSS and the menu stays app-local until a design asks for a shared one. The actions are props,
// so a later action is one more item, never a second menu. `bordered` draws the trigger with the
// darker control border, for a host where it must read as a control (the totals strip, ADO-4037).
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import styles from './BenefitGroupMenu.module.css';

export interface BenefitGroupMenuItem {
  readonly key: string;
  readonly label: string;
  readonly onSelect: () => void;
}

export function BenefitGroupMenu({
  label,
  items,
  bordered = false,
}: {
  /** The trigger's accessible name, for example "Actions for Household". */
  readonly label: string;
  readonly items: readonly BenefitGroupMenuItem[];
  /** Draws the trigger with the darker control border. */
  readonly bordered?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    if (!open) return;
    itemRefs.current[0]?.focus();
    const closeOnOutside = (event: PointerEvent | MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutside);
    return () => document.removeEventListener('mousedown', closeOnOutside);
  }, [open]);

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) trigger.current?.focus();
  };

  const focusItem = (index: number) => {
    const count = items.length;
    if (count === 0) return;
    itemRefs.current[((index % count) + count) % count]?.focus();
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const current = itemRefs.current.findIndex((item) => item === document.activeElement);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusItem(current + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        // With no item focused, ArrowUp starts from the end, as the menu pattern says.
        focusItem(current === -1 ? items.length - 1 : current - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusItem(0);
        break;
      case 'End':
        event.preventDefault();
        focusItem(items.length - 1);
        break;
      case 'Escape':
        event.preventDefault();
        close(true);
        break;
      case 'Tab':
        close(false);
        break;
      default:
    }
  };

  return (
    <div ref={container} className={styles.menuButton}>
      <button
        ref={trigger}
        type="button"
        className={styles.trigger}
        data-bordered={bordered ? 'true' : undefined}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className={styles.dots} aria-hidden />
      </button>
      {open ? (
        <ul
          id={menuId}
          role="menu"
          aria-label={label}
          className={styles.menu}
          onKeyDown={onMenuKeyDown}
        >
          {items.map((item, index) => (
            <li key={item.key} role="none">
              <button
                ref={(element) => {
                  itemRefs.current[index] = element;
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                className={styles.item}
                onClick={() => {
                  close(true);
                  item.onSelect();
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
